import { ConfirmPrintingFileModalComponent } from '../confirm-printing-file-modal/confirm-printing-file-modal.component';
import {  Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { UploadFileToServerComponent } from '@shared/modules/print-file-shared/components/upload-file-to-server/upload-file-to-server.component';
import { allowedFileExtensions } from '@shared/constants/allowedFileExtensions';
import { resourceTypes } from '@shared/constants/resourceTypes';
import { typesUploading } from '@shared/constants/types-uploading';
import { IDevice } from '@shared/interfaces/device/device';
import { IFile } from '@shared/interfaces/file/file';
import { IPrintFileModal } from '@shared/interfaces/file/print-file-modal';
import { IPageModel } from '@shared/interfaces/page-model';
import { ITransferResources } from '@shared/interfaces/resources/transfer-resources';
import { SortByOrderPipe } from '@shared/pipes/sort-by-order.pipe';
import { ApiService } from '@shared/services/api.service';
import { map, Subscription } from 'rxjs';
import { FileDisplayType } from '@shared/modules/file-browser/enums/file-display-type.enum';
import { FileSortFields } from '@shared/modules/file-browser/enums/file-sort-fields.enum';
import { IFileSort } from '@shared/modules/file-browser/interfaces/file-sort.interface';
import { UserService } from '@shared/modules/account/services/user.service';
import { IPrintUploadPreview } from '../../interfaces/print-upload-preview';
import { AlertService } from '@shared/services/alert.service';
import { ErrorCode } from '@shared/enums/error-code.enum';
import { PERMISSIONS } from '@access/permissions';
import { AccessService } from '@access/access.service';

const ICON = {
  Catalog: "folder-outline",
  File: "file-outline"
};

const MAX_RECORD = 10;

@Component({
  selector: 'app-print-file-from-device-modal',
  templateUrl: './print-file-from-device-modal.component.html',
  styleUrls: ['./print-file-from-device-modal.component.scss']
})
export class PrintFileFromDeviceModalComponent implements OnInit, OnDestroy {
  typesUploading: Readonly<any> = typesUploading;
  resourceTypes: Readonly<any> = resourceTypes;

  @ViewChild("uploadFile") uploadFile!: UploadFileToServerComponent;
  @Input() device!: IDevice;
  
  transferResources!: ITransferResources;

  pageNumber = 1;
  currentPath = "/";
  previousPath = "/";

  subscriptions = new Subscription();
  files!: IPageModel<IFile>;
  loadingFiles = true;

  deviceUploadData!: { path: string, deviceId: string };
  confirmationDialogIsActive: boolean = false;

  readonly allowedFileExtensions = allowedFileExtensions.deviceMemory;
  readonly permissions = PERMISSIONS;

  protected sortType: IFileSort = { field: FileSortFields.NAME, direction: 'asc' };
  protected displayType = FileDisplayType.LIST_PREVIEW;
  
  constructor(
    protected dialogRef: NbDialogRef<PrintFileFromDeviceModalComponent>,
    private api: ApiService,
    private alertService: AlertService,
    private dialogService: NbDialogService,
    private sortPipe: SortByOrderPipe,
    private accessService: AccessService
  ) { }

  ngOnInit() {
    this.getTransferResources();
    this.getDeviceFiles();
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  protected get directPrintEnabled(): boolean {
    return this.accessService.hasPermission(PERMISSIONS.Printing.Direct);
  }

  protected get uploadToDeviceDisabled(): boolean {
    const noTransfer = this.transferResources.totalTransfer - this.transferResources.currentTransfer <= 0;
    const noMemory = this.device?.status?.memory?.total === 0;

    return noTransfer || noMemory;
  }

  protected get uploadMethod(): typesUploading {
    const noMemory = this.device?.status?.memory?.total === 0;
    return this.directPrintEnabled && noMemory ? typesUploading.uploadFileToAWS : typesUploading.uploadFileToDevice;
  }
  
  getTransferResources(): void {
    this.subscriptions.add(
      this.api.getTransferResources().subscribe(res => this.transferResources = res)
    );
  }

  onFileUploaded(data: IPrintUploadPreview) {
    this.uploadFile.clearData();

    if (this.uploadMethod === typesUploading.uploadFileToDevice) {
      return;
    }

    if (this.device.type !== data.modelName) {
      this.alertService.danger(ErrorCode.WRONG_DEVICE_TYPE);
      return;
    }
    
    this.confirmationDialogIsActive = true;

    this.dialogService.open(ConfirmPrintingFileModalComponent, {
      context: {
        modalData: {
          noMemory: true,
          device: this.device,
          file: {
            id: data.uploadId,
            name: data.fileName,
            preview: data.filePreview,
            materials: data.materials,
            modelName: data.modelName,
          } as IFile
        }
      }
    }).onClose
      .subscribe(() => this.confirmationDialogIsActive = false);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackByFn(index: number, item: any) {
    return item;
  }

  getDeviceFiles() {
    this.deviceUploadData = { path: this.currentPath, deviceId: this.device.id };

    this.subscriptions.add(
      this.api.getDeviceFiles(this.device.id, MAX_RECORD, this.pageNumber, this.currentPath, "", true)
        .pipe(
          map(r => {
            r.data.forEach(f => {
              f.icon = f.isCatalog ? ICON.Catalog : ICON.File
              return f;
            });

            return r;
          })
        ).subscribe(res => {
          if (this.pageNumber == 1) {
            this.files = res;
          } else {
            this.files.data = [...this.files.data, ...res.data];
          }

          this.files.data = this.sortPipe.transform(this.files.data, 'name');
          this.loadingFiles = false;
        })
    );
  }

  onNext(): void {
    if (this.pageNumber < this.files.totalPages) {
      this.pageNumber++;
      this.getDeviceFiles();
    }
  }

  goBackFromCatalog() {
    this.currentPath = this.previousPath;
    this.changeCatalog(this.currentPath);
  }

  changeCatalog(path: string) {
    this.pageNumber = 1;
    this.previousPath = this.currentPath;
    this.currentPath = path;
    this.loadingFiles = true;
    this.files.data = [];
    this.getDeviceFiles();
  }

  showConfirmation(file: IFile) {
    this.confirmationDialogIsActive = true;

    const modalData: IPrintFileModal = {
      file: file,
      device: this.device
    };
    
    this.dialogService.open<any>(ConfirmPrintingFileModalComponent,
      {
        context: { modalData: modalData },
      }).onClose.subscribe(_ => {
        this.confirmationDialogIsActive = false;
      });
  }

  onScroll(event: any) {
    if (event.target.offsetHeight + event.target.scrollTop > event.target.scrollHeight) {
      if (this.files.totalPages > this.pageNumber) {
        this.pageNumber++;
        this.getDeviceFiles();
      }
    }
  }

  addMoreTransfer(): void {
    this.dialogRef.close({ transferResources: this.transferResources });
  }

  onClickAddMoreResources(): void {
    this.dialogRef.close();
  }
}
